import {
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2,
  Search,
  ShieldCheck,
  ShieldX,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { RefreshButton } from '../../../../../components';
import { Badge } from '../../../../../components/ui/badge';
import { Button } from '../../../../../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../../../components/ui/card';
import { Input } from '../../../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import { formatCnpj, formatCpf, formatDate } from '../../../../../utils';
import {
  BlockedUser,
  BlockedUsersFilters,
  BlockedUsersPagination,
} from '../types';

interface BlockedUsersTableProps {
  users: BlockedUser[];
  pagination: BlockedUsersPagination | undefined;
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  isUpdating: boolean;
  filters: BlockedUsersFilters;
  onPageChange: (page: number) => void;
  onApplyFilters: (filters: BlockedUsersFilters) => void;
  onClearFilters: () => void;
  onToggleBlockStatus: (documento: string, currentBlockAtivo: boolean) => void;
  onRefetch: () => void;
}

export function BlockedUsersTable({
  users,
  pagination,
  isLoading,
  isError,
  error,
  isUpdating,
  filters,
  onPageChange,
  onApplyFilters,
  onClearFilters,
  onToggleBlockStatus,
  onRefetch,
}: BlockedUsersTableProps) {
  const [documentoFilter, setDocumentoFilter] = useState(
    filters.documento || ''
  );
  const [statusFilter, setStatusFilter] = useState<string>(
    filters.blockAtivo === undefined
      ? 'all'
      : filters.blockAtivo
        ? 'blocked'
        : 'unblocked'
  );

  const handleSearch = () => {
    onApplyFilters({
      documento: documentoFilter || undefined,
      blockAtivo:
        statusFilter === 'all' ? undefined : statusFilter === 'blocked',
    });
  };

  const handleClear = () => {
    setDocumentoFilter('');
    setStatusFilter('all');
    onClearFilters();
  };

  const hasActiveFilters =
    filters.documento || filters.blockAtivo !== undefined;

  return (
    <Card className="animate-in fade-in-50 duration-300">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldX className="h-5 w-5" />
              Entregadores Bloqueados
            </CardTitle>
            <CardDescription>
              Gerencie os entregadores bloqueados no sistema
            </CardDescription>
          </div>
          <RefreshButton onClick={onRefetch} loading={isLoading} size="sm" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por CPF ou CNPJ..."
                value={documentoFilter}
                onChange={(e) => setDocumentoFilter(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="blocked">Bloqueados</SelectItem>
              <SelectItem value="unblocked">Desbloqueados</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button onClick={handleSearch} disabled={isLoading}>
              <Search className="h-4 w-4 mr-2" />
              Buscar
            </Button>
            {hasActiveFilters && (
              <Button variant="outline" onClick={handleClear}>
                <X className="h-4 w-4 mr-2" />
                Limpar
              </Button>
            )}
          </div>
        </div>

        {isError && (
          <div className="text-center py-8 text-destructive">
            {error || 'Erro ao carregar dados'}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Documento</TableHead>
                    <TableHead>Operação</TableHead>
                    <TableHead>Motivo</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead>Criado por</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="text-center py-8 text-muted-foreground"
                      >
                        Nenhum registro encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => {
                      const documento = user.cpf
                        ? formatCpf(user.cpf)
                        : user.cnpj
                          ? formatCnpj(user.cnpj)
                          : user.numeroDocumento;

                      return (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">
                            {documento}
                          </TableCell>
                          <TableCell>{user.ops}</TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {user.motivoBloqueio || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                user.blockAtivo ? 'destructive' : 'default'
                              }
                            >
                              {user.blockAtivo ? 'Bloqueado' : 'Desbloqueado'}
                            </Badge>
                          </TableCell>
                          <TableCell>{formatDate(user.createdAt)}</TableCell>
                          <TableCell>{user.createdBy}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant={
                                user.blockAtivo ? 'outline' : 'destructive'
                              }
                              size="sm"
                              onClick={() =>
                                onToggleBlockStatus(
                                  user.numeroDocumento,
                                  user.blockAtivo
                                )
                              }
                              disabled={isUpdating}
                            >
                              {isUpdating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : user.blockAtivo ? (
                                <>
                                  <ShieldCheck className="h-4 w-4 mr-1" />
                                  Desbloquear
                                </>
                              ) : (
                                <>
                                  <ShieldX className="h-4 w-4 mr-1" />
                                  Bloquear
                                </>
                              )}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Mostrando {users.length} de {pagination.total} registros
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Página {pagination.page} de {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                  >
                    Próxima
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
